import React, { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Loading } from './components/loading';
import { useMasterDocumentTranslation } from './i18n';

/** Entrada del menú: acción, encabezado de grupo o separador. */
export interface MoreOption {
  key: string;
  /** Encabezado de grupo: no es pulsable. */
  isHeading?: boolean;
  /** Separador horizontal. */
  isDivider?: boolean;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  /** Texto secundario bajo el rótulo (p. ej. el motivo por el que está deshabilitada). */
  description?: string;
  disabled?: boolean;
  /** Pinta la entrada en el tono de error. */
  danger?: boolean;
  onClick?: () => void;
}

/**
 * Kebab «Más opciones» del canon de MasterCrud (`MasterCrudToolbar`), único
 * dueño de ese control en `MasterDocument`: lo usan la barra del listado y la
 * cabecera del detalle, así que no pueden volver a divergir.
 *
 * Reemplaza al `Dropdown` del kit en estos dos sitios por una razón concreta,
 * no por gusto: con `variant="icon-only"` el `Dropdown` **descarta sus
 * `children`** y pinta siempre su propio chevron (`Dropdown.tsx`, la rama del
 * trigger). La cabecera le pasaba un icono de puntos suspensivos que nunca se
 * llegó a renderizar, y no había forma de poner el indicador de «acción en
 * vuelo» en el disparador — que es donde tiene que ir cuando la acción vive en
 * un menú cerrado.
 *
 * Geometría literal del canon: disparador 36x36 con borde y `rounded-md`; menú
 * anclado a la derecha, 256px, `shadow-md`.
 */
export function MoreOptionsMenu({
  options,
  busy = false,
  ariaLabel,
  className,
}: {
  options: MoreOption[];
  /** Hay una acción del menú en vuelo: el indicador sustituye al icono. */
  busy?: boolean;
  ariaLabel?: string;
  className?: string;
}) {
  const { t } = useMasterDocumentTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const label = ariaLabel ?? t('master_document.list.moreOptions');

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (options.length === 0) return null;

  return (
    <div className={clsx('relative', className)} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={label}
        aria-label={label}
        className={clsx(
          'inline-flex items-center justify-center w-9 h-9 rounded-md transition-colors',
          'border border-border-primary dark:border-dark-border-primary',
          'bg-background-secondary dark:bg-dark-bg-secondary hover:bg-[#ebebec] dark:hover:bg-dark-bg-primary',
          'text-content-tertiary dark:text-dark-content-secondary',
        )}
      >
        {busy
          ? <Loading size="sm" layout="inline" inheritColor ariaLabel={t('master_document.chrome.actionRunning')} />
          : <EllipsisVerticalIcon className="w-4 h-4" aria-hidden="true" />}
      </button>

      {open && (
        <div
          role="menu"
          className={clsx(
            'absolute right-0 top-full mt-1 w-64 z-50 py-1',
            'bg-white dark:bg-dark-bg-primary rounded-md shadow-md',
            'border border-border-primary dark:border-dark-border-primary',
          )}
        >
          {options.map((o) => {
            if (o.isDivider) {
              return <hr key={o.key} className="my-1 border-border-primary dark:border-dark-border-primary" />;
            }
            if (o.isHeading) {
              return (
                <p key={o.key} className="px-3 pt-2 pb-1 text-[11px] font-semibold text-content-tertiary tracking-[0.3px]">
                  {o.label}
                </p>
              );
            }
            return (
              <button
                key={o.key}
                type="button"
                role="menuitem"
                disabled={o.disabled}
                onClick={() => { o.onClick?.(); setOpen(false); }}
                className={clsx(
                  'w-full flex items-start gap-3 px-3 py-2 text-sm text-left transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  o.danger
                    ? 'text-error-content enabled:hover:bg-error-bg/10'
                    : 'text-content-primary dark:text-dark-content-primary enabled:hover:bg-background-secondary dark:enabled:hover:bg-dark-bg-secondary',
                )}
              >
                {o.icon && <span className="w-4 h-4 shrink-0 mt-0.5 inline-flex items-center justify-center">{o.icon}</span>}
                <span className="min-w-0 flex-1">
                  <span className="block">{o.label}</span>
                  {/* El motivo por el que una acción no se puede usar va como
                      texto visible, no como `title`: en un menú, un tooltip
                      obliga a descubrirlo por hover sobre algo que además
                      declara `pointer-events-none` al estar deshabilitado. */}
                  {o.description && (
                    <span className="block text-xs text-content-tertiary dark:text-dark-content-tertiary">
                      {o.description}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
